import {
  Box,
  TextField,
  Typography,
  Stack,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";

const SPEED_OPTIONS = [
  { value: "slow", label: "Slow", rate: "~1/sec" },
  { value: "medium", label: "Medium", rate: "~5/sec" },
  { value: "fast", label: "Fast", rate: "~20/sec" },
];

export default function SendingSettingsSection({ formData, errors, onChange }) {
  const theme = useTheme();

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, mb: 8, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <ScheduleSendIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
          Sending Settings
        </Typography>
      </Stack>

      <Stack spacing={3}>
        {/* Send Mode */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1.25 }}>
            Send Mode
          </Typography>
          <ToggleButtonGroup
            value={formData.sendMode}
            exclusive
            onChange={(_, val) => val && onChange("sendMode", val)}
            size="small"
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                px: 3,
                py: 0.75,
                borderRadius: "20px !important",
                border: "1px solid #e2e8f0 !important",
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  borderColor: `${theme.palette.primary.main} !important`,
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                },
              },
            }}
          >
            <ToggleButton value="instant">Instant</ToggleButton>
            <ToggleButton value="scheduled">Scheduled</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Scheduled Date */}
        {formData.sendMode === "scheduled" && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
                Schedule Date & Time
              </Typography>
              <Chip label="Required" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: theme.palette.primary.main, color: "#fff", textTransform: "uppercase", letterSpacing: 0.5 }} />
            </Stack>
            <TextField
              type="datetime-local"
              fullWidth
              size="small"
              value={formData.scheduledAt || ""}
              onChange={(e) => onChange("scheduledAt", e.target.value)}
              error={!!errors.scheduledAt}
              helperText={errors.scheduledAt}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 16) }}
              sx={inputSx}
            />
          </Box>
        )}

        {/* Sending Speed */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1.25 }}>
            Sending Speed
          </Typography>
          <Stack direction="row" spacing={1.5}>
            {SPEED_OPTIONS.map((opt) => {
              const isSelected = formData.sendSpeed === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => onChange("sendSpeed", opt.value)}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    px: 2,
                    textAlign: "center",
                    borderRadius: 2.5,
                    border: "1.5px solid",
                    borderColor: isSelected ? theme.palette.primary.main : "#e2e8f0",
                    bgcolor: isSelected ? `${theme.palette.primary.main}08` : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: isSelected ? theme.palette.primary.main : "#cbd5e1",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: isSelected ? theme.palette.primary.main : "#475569",
                    }}
                  >
                    {opt.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: isSelected ? theme.palette.primary.main : "#94a3b8",
                      opacity: 0.8,
                    }}
                  >
                    {opt.rate}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Retry Failed */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            px: 2.5,
            borderRadius: 2.5,
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Retry Failed Messages
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
              Number of retry attempts for failed contacts
            </Typography>
          </Box>
          <Select
            value={formData.retryFailed || 0}
            onChange={(e) => onChange("retryFailed", e.target.value)}
            size="small"
            sx={{
              minWidth: 120,
              bgcolor: "#fff",
              borderRadius: 2,
              fontSize: 13,
              fontWeight: 600,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
            }}
          >
            <MenuItem value={0}>No Retry</MenuItem>
            <MenuItem value={1}>1 Retry</MenuItem>
            <MenuItem value={2}>2 Retries</MenuItem>
            <MenuItem value={3}>3 Retries</MenuItem>
          </Select>
        </Box>
      </Stack>
    </Paper>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#f8fafc",
    borderRadius: 2,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};
